import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { DataTableSkeleton, PageHeader, SectionCard, StatCard, StatusPill } from "components/admin";
import { DataTable } from "components/molecules/table/table";
import {
  getSelectableRequestAthletes,
  validateRequestFulfilmentDraft,
  validateRequestPurchaseDraft,
} from "pages/app/workspace/sections/helpers/account-section.helpers";
import {
  RequestActionsForm,
  RequestContextForm,
} from "pages/app/workspace/sections/forms/requests-section-forms";
import {
  SectionContentGrid,
  SectionStatsGrid,
} from "pages/app/workspace/sections/styles/workspace-section.styles";
import { workspaceApi } from "pages/app/workspace/services";
import { formatDateTime, formatMoney, getApiErrorMessage } from "utils/admin";
import {
  canFulfillOwnedAthleteRequests,
  hasPortalAction,
  isAthletePortalOwner,
} from "utils/portal-access";
import { RequestsSectionSection } from "pages/app/workspace/sections/styles/requests-section.inline.styles";
export const RequestsSection: React.FC<{
  currentUser: any;
  portalAccess: any;
}> = ({ currentUser, portalAccess }) => {
  const queryClient = useQueryClient();
  const [selectedPlayerId, setSelectedPlayerId] = useState(currentUser?.player?.id || "");
  const [requestTitle, setRequestTitle] = useState("");
  const [requestDescription, setRequestDescription] = useState("");
  const [requestPrice, setRequestPrice] = useState("");
  const [requestToFulfill, setRequestToFulfill] = useState("");
  const athleteOwnedAccount = isAthletePortalOwner(currentUser, portalAccess);
  const athletesQuery = useQuery({
    queryKey: ["workspace-requests-athletes"],
    queryFn: () => workspaceApi.athletes.list(),
  });
  const requestsQuery = useQuery({
    queryKey: ["workspace-requests", selectedPlayerId],
    queryFn: () =>
      workspaceApi.requests.list(selectedPlayerId, {
        page: 1,
        per_page: 50,
      }),
    enabled: Boolean(selectedPlayerId),
  });
  const availableQuery = useQuery({
    queryKey: ["workspace-request-templates", selectedPlayerId],
    queryFn: () => workspaceApi.requests.available(selectedPlayerId),
    enabled: Boolean(selectedPlayerId),
  });
  const createMutation = useMutation({
    mutationFn: () =>
      workspaceApi.requests.create(selectedPlayerId, {
        title: requestTitle,
        description: requestDescription,
        price: Number(requestPrice),
      }),
    onSuccess: () => {
      toast.success("Request purchased successfully.");
      setRequestTitle("");
      setRequestDescription("");
      setRequestPrice("");
      queryClient.invalidateQueries({
        queryKey: ["workspace-requests", selectedPlayerId],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-wallet"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to create this request."));
    },
  });
  const cancelMutation = useMutation({
    mutationFn: (id: string | number) => workspaceApi.requests.cancel(id),
    onSuccess: () => {
      toast.success("Request cancelled.");
      queryClient.invalidateQueries({
        queryKey: ["workspace-requests", selectedPlayerId],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-wallet"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to cancel this request."));
    },
  });
  const fulfillMutation = useMutation({
    mutationFn: () => workspaceApi.requests.fulfill(requestToFulfill),
    onSuccess: () => {
      toast.success("Request marked as fulfilled.");
      setRequestToFulfill("");
      queryClient.invalidateQueries({
        queryKey: ["workspace-requests", selectedPlayerId],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to fulfil this request."));
    },
  });
  const athletes = athletesQuery.data || [];
  const availableTemplates = availableQuery.data || [];
  const requests = requestsQuery.data || [];
  const canCreateRequests = hasPortalAction(portalAccess, "requests.create");
  const canManageRequests = hasPortalAction(portalAccess, "requests.manage");
  const canFulfillRequests = canFulfillOwnedAthleteRequests(currentUser, portalAccess);
  const canCancelRequests = canCreateRequests || canManageRequests;
  const requestPurchaseErrors = validateRequestPurchaseDraft({
    requestPrice,
    requestTitle,
    selectedPlayerId,
  });
  const requestFulfilmentErrors = validateRequestFulfilmentDraft(requestToFulfill);
  const selectableAthletes = getSelectableRequestAthletes({
    athleteOwnedAccount,
    athletes,
    canCreateRequests,
    canManageRequests,
    currentUser,
  });
  useEffect(() => {
    if (
      athleteOwnedAccount &&
      currentUser?.player?.id &&
      selectedPlayerId !== currentUser.player.id
    ) {
      setSelectedPlayerId(currentUser.player.id);
    }
  }, [athleteOwnedAccount, currentUser?.player?.id, selectedPlayerId]);
  const headerTitle = athleteOwnedAccount ? "Request Operations" : "Requests";
  const headerDescription = athleteOwnedAccount
    ? "Review the player account request tools available today. Athlete logins can stay scoped to their own profile and fulfil requests when they already have the request ID."
    : "Use the request endpoints to inspect templates, purchase new requests for an athlete, and cancel or fulfil requests when your account is allowed to.";
  const requestsCountLabel = athleteOwnedAccount ? "Visible Requests" : "My Requests";
  const fulfilmentModeLabel = athleteOwnedAccount ? "Request Fulfilment" : "Fulfillable";
  const fulfilmentModeValue = athleteOwnedAccount ? "By request ID" : "Client mode";
  const listEmptyDescription = athleteOwnedAccount
    ? "The current API does not yet return an incoming request inbox for athlete-owned accounts, so requests only appear here when the backend exposes them."
    : "Requests for the selected athlete will appear here once they are purchased.";
  const handleCreateSubmit = () => {
    const firstError =
      requestPurchaseErrors.selectedPlayerId ||
      requestPurchaseErrors.requestTitle ||
      requestPurchaseErrors.requestPrice;
    if (firstError) {
      toast.error(firstError);
      return;
    }
    createMutation.mutate();
  };
  const handleFulfilSubmit = () => {
    if (requestFulfilmentErrors.requestToFulfill) {
      toast.error(requestFulfilmentErrors.requestToFulfill);
      return;
    }
    fulfillMutation.mutate();
  };
  return (
    <>
      <PageHeader title={headerTitle} description={headerDescription} />

      <SectionStatsGrid>
        <StatCard
          label={athleteOwnedAccount ? "My Athlete" : "Selected Athlete"}
          value={selectedPlayerId ? "Connected" : "Choose one"}
        />
        <StatCard
          label="Templates"
          value={athleteOwnedAccount ? "Read-only" : availableTemplates.length}
        />
        <StatCard label={requestsCountLabel} value={requests.length} />
        <StatCard label={fulfilmentModeLabel} value={fulfilmentModeValue} />
      </SectionStatsGrid>

      <RequestsSectionSection>
        <SectionContentGrid>
          <SectionCard
            subtitle={
              athleteOwnedAccount
                ? "Your athlete profile stays locked to your own record"
                : "Choose the player context"
            }
            title={athleteOwnedAccount ? "Athlete Context" : "Player Context"}
          >
            <RequestContextForm
              athleteOwnedAccount={athleteOwnedAccount}
              availableTemplates={availableTemplates}
              onPlayerChange={setSelectedPlayerId}
              playerError={requestPurchaseErrors.selectedPlayerId}
              selectableAthletes={selectableAthletes}
              selectedPlayerId={selectedPlayerId}
            />
          </SectionCard>

          <SectionCard
            subtitle={
              athleteOwnedAccount
                ? "Fulfil request IDs shared by ops or support"
                : "Create or fulfil a request"
            }
            title={athleteOwnedAccount ? "Fulfilment Tools" : "Actions"}
          >
            <RequestActionsForm
              athleteOwnedAccount={athleteOwnedAccount}
              canCreateRequests={canCreateRequests}
              canFulfillRequests={canFulfillRequests}
              createPending={createMutation.isPending}
              fulfillPending={fulfillMutation.isPending}
              onCreate={handleCreateSubmit}
              onFulfill={handleFulfilSubmit}
              onRequestDescriptionChange={setRequestDescription}
              onRequestPriceChange={setRequestPrice}
              onRequestTitleChange={setRequestTitle}
              onRequestToFulfillChange={setRequestToFulfill}
              requestDescription={requestDescription}
              requestPriceError={requestPurchaseErrors.requestPrice}
              requestPrice={requestPrice}
              requestTitleError={requestPurchaseErrors.requestTitle}
              requestTitle={requestTitle}
              requestToFulfillError={requestFulfilmentErrors.requestToFulfill}
              requestToFulfill={requestToFulfill}
              selectedPlayerId={selectedPlayerId}
            />
          </SectionCard>
        </SectionContentGrid>
      </RequestsSectionSection>

      <RequestsSectionSection>
        <DataTable
          canEdit={false}
          columns={[
            {
              label: "Title",
              accessor: (row: any) => row.title,
            },
            {
              label: "Price",
              accessor: (row: any) => formatMoney(row.price),
            },
            {
              label: "Status",
              accessor: (row: any) => <StatusPill value={row.status} />,
            },
            {
              label: "Created",
              accessor: (row: any) => formatDateTime(row.created_at),
            },
          ]}
          data={requests}
          emptyDescription={listEmptyDescription}
          emptyTitle={
            athleteOwnedAccount
              ? "No athlete request inbox returned yet"
              : "No requests for this athlete"
          }
          isLoading={requestsQuery.isLoading}
          loadingState={<DataTableSkeleton columns={4} rows={5} />}
          onRemove={
            canCancelRequests
              ? (id, row) => {
                  const targetId = row?.id || id;
                  if (window.confirm("Cancel this request?")) {
                    cancelMutation.mutate(String(targetId));
                  }
                }
              : undefined
          }
        />
      </RequestsSectionSection>
    </>
  );
};
